import moment from 'moment';
import { getMetricDisplayName } from '@utils/metrics';
import { formatEndedStatus } from '@utils/humanize';
import { PRODUCT_MODE_MAP } from '@src/utils/constants';
import alphanumericSort from '@src/utils/alphanumericSort';
import { formatMilliSecs, formatSecs, formatDatatoSecs, formatKbps } from './formatUtils';

const DateFormatterStyle = 'MMM DD YYYY, HH:mm:ss';

export const columnCategory = {
  basicInformation: 'Basic Information',
  startupMetrics: 'Startup Metrics',
  lifetimePlaybackMetrics: 'Lifetime Playback Metrics',
  intervalPlaybackMetrics: 'Interval Playback Metrics',
};

export const columnsMetrics = {
  [columnCategory.basicInformation]: [
    'SessionId',
    'SessionStartTime',
    'SessionEndTime',
    'AssetName',
    'Status',
    'IsEnded',
    'ImpactedSession',
    'ISP',
    'ASN',
    'tags.m3.dv.osf',
    'tags.channel',
    'ContentLengthSecs',
    'tags.c3.video.isLive',
    'Ip',
  ],
  [columnCategory.startupMetrics]: [
    'LifeJoinTimeSecs',
    'ExitBeforeVideoStart',
    'LifeJoinedAvgBitrateKbps',
    'VideoStartFailure',
    'VideoStartFailureBusiness',
    'VideoStartFailureTech',
    'VideoPlaybackFailure',
    'VideoPlaybackFailureBusiness',
    'VideoPlaybackFailureTech',
    'VideoStartFailureErrorCode',
    'VideoStartFailureBusinessErrorCode',
    'VideoStartFailureTechErrorCode',
    'VideoPlaybackFailureErrorCode',
    'VideoPlaybackFailureBusinessErrorCode',
    'VideoPlaybackFailureTechErrorCode',
  ],
  [columnCategory.lifetimePlaybackMetrics]: [
    'PlayingTime',
    'PercentageComplete',
    'LifeRebufferingRatio',
    'LifeNetworkRebufferingRatio',
    'LifeBufferingTimeSecs',
    'LifeAvgBitrateKbps',
    'LifeAverageAvgBitrateKbps',
    'LifeAvgFramesPerSecs',
    'LifeJoinedAvgBitrateKbps',
    'LifeVideoRestartTimeSecs',
    'LifePausedTimePct',
    'LifePausedTimeSecs',
    'EndedStatus',
    'LifeLastPlayHeadTimeMs',
    'LifeNumBitrateSwitches',
    'LifeExitRelatedCIRTimeMs',
    'IsExitDueToCIR',
    'LifeEncodedFrames',
    'LifeRenderedFrames',
    'LifeDropFrameRatio',
  ],
  [columnCategory.intervalPlaybackMetrics]: ['LifeCDNs'],
};

export const getColumnsNameReflection = () => ({
  SessionId: 'Session ID',
  ViewerId: 'Viewer ID',
  SessionStartTime: 'Session Start Time',
  SessionEndTime: 'Session End Time',
  ISP: 'ISP',
  ASN: 'ASN',
  AssetName: 'Asset Name',
  ContentLengthSecs: 'Content Length',
  PlayingTime: 'Playing Time',
  PercentageComplete: 'Average % Complete',
  LifeBufferingTimeSecs: 'Rebuffering Time',
  LifeResources: 'Resources',
  EndedStatus: 'Ended Status',
  LifeLastPlayHeadTimeMs: 'Last Playhead Time',
  IntervalPlaytimeSecs: 'Playing Time for the minute',
  IntervalRebufferingTimeSecs: 'Rebuffering Time for the selected minute',
  IntervalNetworkRebufferingTimeSecs: 'Connection Induced Rebuffering Time for the selected minute',
  LifeCDNs: 'All CDNs',
  IntervalResource: 'Resource for the minute',
  Status: 'Status',
  ImpactedSession: 'Impacted Session',
  IsEnded: 'Ended Session',
  Ip: 'IP Address',
  'tags.m3.dv.n': 'Device Name',
  'tags.m3.dv.osf': 'Device Operating System',
  'tags.c3.video.isLive': 'Content Category',
  'tags.channel': 'Channel Name',
  'geo.country': 'Countries',
  'geo.state': 'States',
  'geo.continent': 'Continents',
  'geo.dma': 'DMA',
  'geo.isp': 'ISP',
  'geo.city': 'Cities',
  'geo.postalCode': 'Postal Code',
  LifeExitRelatedCIRTimeMs: 'CIR Time preceding Exit',
  IsExitDueToCIR: 'CIR Related Exits',
  LifeEncodedFrames: 'Encoded Frames',
  LifeRenderedFrames: 'Rendered Frames',
  LifeDropFrameRatio: 'Dropped Frames Ratio',
  LifeRebufferingRatio: getMetricDisplayName({
    metricKey: 'RebufferingRatio',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeJoinTimeSecs: getMetricDisplayName({
    metricKey: 'VideoStartTime',
    context: PRODUCT_MODE_MAP.EI,
  }),
  ExitBeforeVideoStart: getMetricDisplayName({
    metricKey: 'ExitBeforeVideoStarts',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailure: getMetricDisplayName({
    metricKey: 'VideoStartFailures',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailureBusiness: getMetricDisplayName({
    metricKey: 'VideoStartFailuresBusiness',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailureTech: getMetricDisplayName({
    metricKey: 'VideoStartFailuresTech',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoPlaybackFailure: getMetricDisplayName({
    metricKey: 'VideoPlaybackFailures',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoPlaybackFailureBusiness: getMetricDisplayName({
    metricKey: 'VideoPlaybackFailuresBusiness',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoPlaybackFailureTech: getMetricDisplayName({
    metricKey: 'VideoPlaybackFailuresTech',
    context: PRODUCT_MODE_MAP.EI,
  }),
  VideoStartFailureErrorCode: `${getMetricDisplayName({ metricKey: 'VideoStartFailures', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoStartFailureBusinessErrorCode: `${getMetricDisplayName({ metricKey: 'VideoStartFailuresBusiness', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoStartFailureTechErrorCode: `${getMetricDisplayName({ metricKey: 'VideoStartFailuresTech', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoPlaybackFailureErrorCode: `${getMetricDisplayName({ metricKey: 'VideoPlaybackFailures', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoPlaybackFailureBusinessErrorCode: `${getMetricDisplayName({ metricKey: 'VideoPlaybackFailuresBusiness', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  VideoPlaybackFailureTechErrorCode: `${getMetricDisplayName({ metricKey: 'VideoPlaybackFailuresTech', context: PRODUCT_MODE_MAP.EI })} Error Code`,
  LifeAvgBitrateKbps: getMetricDisplayName({ metricKey: 'Bitrate', context: PRODUCT_MODE_MAP.EI }),
  LifeAverageAvgBitrateKbps: getMetricDisplayName({
    metricKey: 'AvgBitrate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeAvgFramesPerSecs: getMetricDisplayName({
    metricKey: 'Framerate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeVideoRestartTimeSecs: `Total ${getMetricDisplayName({ metricKey: 'VideoRestartTime', context: PRODUCT_MODE_MAP.EI })}`,
  LifeNetworkRebufferingRatio: getMetricDisplayName({
    metricKey: 'ConnectionInducedRebufferingRatio',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifePausedTimeSecs: getMetricDisplayName({
    metricKey: 'LifePausedTimeMins',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifePausedTimePct: getMetricDisplayName({
    metricKey: 'LifePausedTimePct',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeNumBitrateSwitches: getMetricDisplayName({
    metricKey: 'LifeAvgNumBitrateSwitches',
    context: PRODUCT_MODE_MAP.EI,
  }),
  LifeJoinedAvgBitrateKbps: getMetricDisplayName({
    metricKey: 'LifeJoinedAvgBitrate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  IntervalAverageAvgBitrateKbps: `${getMetricDisplayName({ metricKey: 'AvgBitrate', context: PRODUCT_MODE_MAP.EI })} for the selected minute`,
  IntervalAvgBitrateKbps: `${getMetricDisplayName({ metricKey: 'Bitrate', context: PRODUCT_MODE_MAP.EI })} for the selected minute`,
  IntervalAvgFramesPerSec: `${getMetricDisplayName({ metricKey: 'Framerate', context: PRODUCT_MODE_MAP.EI })} for the minute`,
  IntervalVideoRestartTimeSecs: `${getMetricDisplayName({ metricKey: 'VideoRestartTime', context: PRODUCT_MODE_MAP.EI })} for the selected minute`,
});

export const metricList = [
  'SessionId',
  'SessionStartTime',
  'SessionEndTime',
  'PlayingTime',
  'AssetName',
  'Status',
  'IsEnded',
  'ImpactedSession',
];

export const MAX_ANCHORED_COUNT = 3;

export const GROUPBY_MAPPING = {
  GEO_CITY: 'geo.city',
  GEO_COUNTRY: 'geo.country',
  GEO_DMA: 'geo.dma',
  GEO_ISP: 'ISP',
  GEO_ASN: 'ASN',
  GEO_CONTINENT: 'geo.continent',
  GEO_STATE: 'geo.state',
  GEO_POSTAL_CODE: 'geo.postalCode',
  OBJECT_NAME: 'AssetName',
  LIVE_VIDEO: 'tags.c3.video.isLive',
  RESOURCE_ID: 'LifeResources',
};

export const sortAndSearchTable = (key, searchText, sort, data, orderKey) => {
  let finalData = data;
  const re = new RegExp(searchText, 'i');
  // Use key to search text in data
  if (key) {
    // key can be tags$m3$dv$os which is actually tags.m3.dv.os
    const keyFixed = key.replace(/\$/g, '.');
    finalData = finalData.filter((item) => item[keyFixed] && re.test(item[keyFixed]));
  }
  // Use orderKey to sort existing data
  if (orderKey) {
    finalData = finalData.sort((prev, next) =>
      alphanumericSort(sort, prev[replaceKey(orderKey)], next[replaceKey(orderKey)]) ? 1 : -1,
    );
  }
  return finalData;
};

export const replaceKey = (key) => key.replace(/\./g, '$');

export const replaceFinalDataKey = (finalData) =>
  finalData.map((data) =>
    Object.fromEntries(Object.keys(data).map((key) => [replaceKey(key), data[key]])),
  );

export const defaultSubscriberColumns = () => {
  const updatedMetricColumns = metricList.map((item) => ({ metric: item, show: true }));
  return updatedMetricColumns;
};

export const columnValueFormatter = (v, type) => {
  let value;
  switch (type) {
    case 'SessionStartTime':
    case 'SessionEndTime':
      if (v !== '-') {
        value = moment(v, 'x').format(DateFormatterStyle);
      } else {
        value = v;
      }
      break;
    case 'ContentLengthSecs':
      value = formatSecs(v);
      break;
    case 'LifeLastPlayHeadTimeMs':
    case 'PlayingTime':
    case 'LifePausedTimeSecs':
    case 'LifeExitRelatedCIRTimeMs':
      value = formatMilliSecs(v);
      break;
    case 'EndedStatus':
      value = formatEndedStatus(v);
      break;
    case 'LifeJoinTimeSecs':
    case 'LifeBufferingTimeSecs':
    case 'LifeVideoRestartTimeSecs':
    case 'IntervalPlaytimeSecs':
    case 'IntervalRebufferingTimeSecs':
    case 'IntervalNetworkRebufferingTimeSecs':
    case 'IntervalVideoRestartTimeSecs':
    case 'VideoStartTime':
    case 'VideoRestartTime':
      value = formatDatatoSecs(v);
      break;
    case 'LifeRebufferingRatio':
    case 'PercentageComplete':
    case 'LifeNetworkRebufferingRatio':
    case 'RebufferingRatio':
    case 'ConnectionInducedRebufferingRatio':
    case 'LifePausedTimePct':
    case 'LifeDropFrameRatio':
      value = `${Number(v).toFixed(2)} %`;
      break;
    case 'LifeAvgBitrateKbps':
    case 'LifeAverageAvgBitrateKbps':
    case 'IntervalAvgBitrateKbps':
    case 'IntervalAverageAvgBitrateKbps':
    case 'LifeJoinedAvgBitrateKbps':
      value = formatKbps(Number(v));
      break;
    case 'Bitrate':
      value = formatKbps(Number(v / 1000));
      break;
    case 'LifeAvgFramesPerSecs':
      value = `${Number(v)} FPS`;
      break;
    default:
      value = v !== '' ? v : '-';
      break;
  }
  return value;
};
